/**
 * Domain Detail Section Component
 * Displays detailed information for a domain or domain bridge:
 * value ratio chart, priorities panel, resources panel. Used in Control Bridge main content.
 * @feature CONTROL_BRIDGE_ACCESS - Control Bridge domain detail section
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Typography, Button } from 'antd';
import { LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { ValueBreakdownChart, ChartPeriodData } from '../Dashboard/ReportingComponents/ValueBreakdownChart';
import { PriorityControlPanel } from './PriorityControlPanel';
import { ResourceControlPanel } from './ResourceControlPanel';
import styles from './DomainDetailSection.module.css';

const { Title } = Typography;

export interface DomainDetailSectionProps {
  /** Domain/Bridge ID */
  domainId: string;
  /** Domain/Bridge name */
  domainName: string;
  /** Domain/Bridge icon */
  icon: React.ReactNode;
  /** Domain/Bridge color */
  color: string;
  /** Chart data for value ratio over time */
  chartData: ChartPeriodData[];
  /** Priorities data */
  priorities?: Array<{
    id: string;
    name: string;
    sequence: number;
    enabled: boolean;
    type: 'improvement' | 'operational';
    domain_artifact?: string;
    resource_name?: string;
    start_date?: string;
    end_date?: string;
    duration?: string;
    remaining_duration?: string;
  }>;
  /** Resources data */
  resources?: Array<{
    id: string;
    name: string;
    type: string;
    allocated: number;
    available: number;
  }>;
  /** Callback when priorities change */
  onPrioritiesChange?: (priorities: DomainDetailSectionProps['priorities']) => void;
  /** Callback when resources change */
  onResourcesChange?: (resources: DomainDetailSectionProps['resources']) => void;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Whether to use compact layout */
  compact?: boolean;
  /** View mode selector (shared across all domains) */
  viewMode?: 'performance' | 'priorities' | 'resources';
  /** Link to business objective (shown above priorities when domain is business) */
  businessObjectiveLink?: { url: string; label: string };
  /** Bridge cards to show in priorities (when domain is business) */
  bridgeCards?: Array<{ id: string; sessionId: string; name: string }>;
  /** Business / product bridge card planks shown in Priorities */
  businessBridgeCardsWithPlanks?: Array<{
    sessionId: string;
    productName: string;
    planks: Array<{
      question_text: string;
      action?: string;
      assignee?: string;
      duration?: string;
      future_condition?: string;
    }>;
  }>;
  productBridgeCardsWithPlanks?: Array<{
    sessionId: string;
    productName: string;
    planks: Array<{
      question_text: string;
      action?: string;
      assignee?: string;
      duration?: string;
      future_condition?: string;
    }>;
  }>;
  /** Callback for "Conduct Bridge Session" button */
  onConductBridgeSession?: () => void;
  /** Domain filter for priorities (Strategy, Finance, Operations, Revenue, Resource) */
  domainFilter?: string | null;
  /** Callback when domain filter changes */
  onDomainFilterChange?: (domain: string | null) => void;
  /** Company ID (for business domain Add Resource - create employee) */
  companyId?: string | null;
}

export const DomainDetailSection: React.FC<DomainDetailSectionProps> = ({
  domainId,
  domainName,
  icon,
  color,
  chartData,
  priorities = [],
  resources = [],
  onPrioritiesChange,
  onResourcesChange,
  disabled = false,
  compact = false,
  viewMode = 'performance',
  businessObjectiveLink,
  bridgeCards = [],
  businessBridgeCardsWithPlanks = [],
  productBridgeCardsWithPlanks = [],
  onConductBridgeSession,
  domainFilter = null,
  onDomainFilterChange,
  companyId = null,
}) => {
  return (
    <div className={`${styles.section} ${compact ? styles.compact : ''}`} style={{ '--domain-color': color } as React.CSSProperties}>
      {/* Section Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.iconContainer} style={{ backgroundColor: `${color}20`, color }}>
            {icon}
          </div>
          <Title level={4} className={styles.title}>
            {domainName}
          </Title>
        </div>
      </div>

      {/* Content based on view mode */}
      <div className={styles.content}>
        {viewMode === 'performance' && (
          <div className={styles.tabContent}>
            <div className={styles.chartContainer}>
              <ValueBreakdownChart
                data={chartData}
                height={compact ? 180 : 300}
                valueColor={color}
                costColor="#ef4444"
              />
            </div>
          </div>
        )}
        {viewMode === 'priorities' && (
          <div className={styles.tabContent}>
            {domainId === 'business' && businessObjectiveLink && (
              <div className={styles.businessObjectiveLink}>
                <Link to={businessObjectiveLink.url}>
                  <LinkOutlined /> {businessObjectiveLink.label}
                </Link>
              </div>
            )}
            {domainId === 'business' && bridgeCards.length > 0 && (
              <div className={styles.bridgeCardsList}>
                <Title level={5}>Bridge Cards</Title>
                <ul>
                  {bridgeCards.map((bc) => (
                    <li key={bc.id}>
                      <Link to={`/bridge-session/${bc.sessionId}?step=bridge_card`}>{bc.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {domainId === 'business' && businessBridgeCardsWithPlanks.length > 0 && (
              <div className={styles.bridgeCardsList}>
                <Title level={5}>Action Planks (Business Bridge)</Title>
                {businessBridgeCardsWithPlanks.map((card) => (
                  <div key={card.sessionId} style={{ marginBottom: 16 }}>
                    <Link to={`/bridge-session/${card.sessionId}?step=bridge_card`}>
                      <strong>{card.productName}</strong>
                    </Link>
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      {card.planks.map((plank, idx) => (
                        <li key={idx} style={{ marginBottom: 4 }}>
                          <span>{plank.question_text}</span>
                          {plank.action && (
                            <span style={{ color: '#595959', marginLeft: 8 }}>
                              → {plank.action}
                              {plank.assignee && ` (${plank.assignee})`}
                              {plank.duration && ` [${plank.duration}]`}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {domainId === 'product-bridge' && productBridgeCardsWithPlanks.length > 0 && (
              <div className={styles.bridgeCardsList}>
                <Title level={5}>Action Planks (Product Bridge)</Title>
                {productBridgeCardsWithPlanks.map((card) => (
                  <div key={card.sessionId} style={{ marginBottom: 16 }}>
                    <Link to={`/local-bridge/product-domain-bridge/${card.sessionId}?step=bridge_card`}>
                      <strong>{card.productName}</strong>
                    </Link>
                    <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                      {card.planks.map((plank, idx) => (
                        <li key={idx} style={{ marginBottom: 4 }}>
                          <span>{plank.question_text}</span>
                          {plank.action && (
                            <span style={{ color: '#595959', marginLeft: 8 }}>
                              → {plank.action}
                              {plank.assignee && ` (${plank.assignee})`}
                              {plank.duration && ` [${plank.duration}]`}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {domainId === 'business' && onConductBridgeSession && (
              <div className={styles.conductBridgeButton}>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={onConductBridgeSession}
                  disabled={disabled}
                >
                  Conduct Bridge Session
                </Button>
              </div>
            )}
            <PriorityControlPanel
              domainId={domainId}
              priorities={priorities}
              onPrioritiesChange={onPrioritiesChange}
              disabled={disabled}
              compact={compact}
              domainFilter={domainFilter}
              onDomainFilterChange={onDomainFilterChange}
            />
          </div>
        )}
        {viewMode === 'resources' && (
          <div className={styles.tabContent}>
            <ResourceControlPanel
              domainId={domainId}
              resources={resources}
              onResourcesChange={onResourcesChange}
              disabled={disabled}
              compact={compact}
              companyId={domainId === 'business' ? companyId : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainDetailSection;
